import React, {
  useCallback,
  useState,
} from 'react'
import {
  Button,
  Stack,
  Typography,
} from '@mui/material'

import {
  api,
  lang,
} from '@common-sense-privacy/common'

import useRouteParams from '@/hooks/useRouteParams'
import { useCurrentOrganization } from '@/hooks/useSession'

import { usePutProductPrivacyIssueMutation } from '@/services/organization/product'

import {
  Answer,
  Choice,
  Props,
} from './types'

const { POLICY_EVALUATION_ANSWER_SOURCE } = api.endpoints.organization.product.privacyIssue.types

export const YES_LABEL = 'Yes'

export const NO_LABEL = 'No'

export const SKIP_LABEL = 'I don\'t know'

export const CHOICE_LABELS: Record<Choice, string> = {
  no: NO_LABEL,
  skipped: SKIP_LABEL,
  yes: YES_LABEL,
}

export const CHOICES: Choice[] = [
  'yes',
  'no',
  'skipped',
]

/**
 * Which button a stored answer lights up. `currentAnswer` is the option VALUE saved
 * against the question, which is free text on the entity, so only a value that reads as
 * yes/no can be matched to a button - anything else marks none of the three.
 */
export const CURRENT_ANSWER_CHOICES: Record<string, Choice> = {
  no: 'no',
  yes: 'yes',
}

export const currentAnswerChoice = (currentAnswer: string | null): Choice | undefined => (currentAnswer
  ? CURRENT_ANSWER_CHOICES[currentAnswer.trim().toLowerCase()]
  : undefined)

/**
 * The choice already stored against the question, or undefined when it has none. A
 * skip saves no answer value, so it is only visible through `isSkipped` - without that
 * a saved "I don't know" would look unanswered on every return to the screen.
 */
export const storedChoice = ({
  currentAnswer,
  isSkipped,
}: Pick<Answer, 'currentAnswer' | 'isSkipped'>): Choice | undefined => currentAnswerChoice(currentAnswer)
  ?? (isSkipped ? 'skipped' : undefined)

/**
 * Only a `yes` or a `no` prediction marks a button. An `unclear` prediction marks none
 * of them and still offers all three - the policy being silent does not mean the user
 * does not know the answer.
 */
export const SUGGESTED_CHOICES: Record<string, Choice> = {
  no: 'no',
  yes: 'yes',
}

export const answerActionsTestId = (code: string): string => `policy-evaluation-actions-${code}`

export const answerButtonTestId = (code: string, choice: Choice): string => `policy-evaluation-answer-${code}-${choice}`

/**
 * Accepting a suggestion writes a REAL wizard answer through the ordinary privacy-issue
 * PUT - `{ answers: [optionId] }` for yes/no, and the existing skip path `{ answers: [] }`
 * for "I don't know". That endpoint is what applies branch unlocking, the N/A collapse and
 * the product score recalculation, so nothing here may shortcut it with a bespoke write.
 *
 * Answering is not a one-way door: the same PUT overwrites a previous answer, so the
 * buttons stay live afterwards and a mis-click is one click away from being corrected.
 * The only thing that disables them is a save already in flight.
 *
 * Every write here carries `source: 'policy-evaluation'`. The wizard bookmarks the last
 * question answered so "Resume" can return to it, and an answer accepted on this screen
 * must not move that bookmark - accepting 1.8.1 here would otherwise resume the section
 * past every question before it.
 */
function AnswerActions({ answer }: Props): React.ReactElement {
  const {
    code,
    noOptionId,
    prediction,
    productPrivacyIssueId,
    yesOptionId,
  } = answer

  const { id: organizationId } = useCurrentOrganization()
  const { productId } = useRouteParams()

  const [
    putProductPrivacyIssue,
    { isLoading: isSaving },
  ] = usePutProductPrivacyIssueMutation()

  const [
    savedAnswer,
    setSavedAnswer,
  ] = useState<Choice>()

  const [
    saveError,
    setSaveError,
  ] = useState<string>()

  const suggestedChoice = SUGGESTED_CHOICES[prediction.trim().toLowerCase()]

  const handleAnswer = useCallback((choice: Choice) => {
    setSaveError(undefined)

    const answers = {
      no: [ noOptionId ],
      // The empty array IS the skip: the endpoint clears any stored answers and moves the
      // question to status 'skipped'.
      skipped: [],
      yes: [ yesOptionId ],
    }[choice]

    putProductPrivacyIssue({
      body: {
        answers,
        source: POLICY_EVALUATION_ANSWER_SOURCE,
      },
      params: {
        id: organizationId,
        // The PUT's `privacyIssueId` param is really the ProductPrivacyIssue id.
        privacyIssueId: productPrivacyIssueId,
        productId: `${productId}`,
      },
    })
      .unwrap()
      .then(() => setSavedAnswer(choice))
      // A failed change leaves the previously saved answer standing - both on screen and
      // in the database - and `isSaving` going false re-enables the buttons on its own,
      // so a retry is one click away.
      .catch(error => setSaveError(error?.data?.message || lang().messages.somethingWentWrong()))
  }, [
    noOptionId,
    organizationId,
    productId,
    productPrivacyIssueId,
    putProductPrivacyIssue,
    yesOptionId,
  ])

  // An answer the user already gave - in the wizard, on an earlier visit to this
  // screen, or the one the analysis wrote when the evaluation completed - is
  // indistinguishable from one saved a moment ago, and reads the same way.
  const selectedChoice = savedAnswer ?? storedChoice(answer)

  // What fills a button is the answer stored against the question, so a freshly
  // completed evaluation lights up the button its prediction maps to. The callout
  // above keeps saying what the analysis suggested and out of what, and the button
  // it matches is additionally tagged with `data-suggested`.

  const handleClick = useCallback((choice: Choice) => {
    // Re-picking the answer already stored would be a pointless write, and one that could
    // fail and put an error under an answer that never changed.
    if (choice === selectedChoice) {
      return
    }

    handleAnswer(choice)
  }, [
    handleAnswer,
    selectedChoice,
  ])

  return (
    <Stack spacing={1} data-testid={answerActionsTestId(code)}>
      <Stack direction='row' spacing={1.5} flexWrap='wrap' useFlexGap={true}>
        {CHOICES.map(choice => (
          <Button
            key={choice}
            aria-pressed={selectedChoice === choice}
            data-selected={selectedChoice === choice ? 'true' : undefined}
            data-suggested={suggestedChoice === choice ? 'true' : undefined}
            data-testid={answerButtonTestId(code, choice)}
            disabled={isSaving}
            onClick={() => handleClick(choice)}
            variant={selectedChoice === choice ? 'contained' : 'outlined'}
            sx={{ width: 'auto' }}
          >
            {CHOICE_LABELS[choice]}
          </Button>
        ))}
      </Stack>
      {!!saveError && (
        <Typography variant='body2' color='error' role='alert'>{saveError}</Typography>
      )}
    </Stack>
  )
}

export default React.memo(AnswerActions)
